/**
 * Safe consumer identity resolution for post-generation navigation.
 * Avoids ReferenceError when completion handlers run outside buildGenerationRequest scope.
 */

export function resolveConsumerNameForCompletion({
  resolvedResult = null,
  result = null,
  reportData = null,
  user = null,
  nameAnchor = null,
} = {}) {
  return (
    resolvedResult?.consumer_info?.name ||
    result?.consumer_info?.name ||
    nameAnchor?.correct_value ||
    reportData?.personal_info?.name ||
    user?.name ||
    user?.full_name ||
    ''
  );
}

export function resolveConsumerDobForCompletion({
  resolvedResult = null,
  result = null,
  reportData = null,
  dobAnchor = null,
} = {}) {
  return (
    resolvedResult?.consumer_info?.dob ||
    resolvedResult?.consumer_info?.date_of_birth ||
    resolvedResult?.consumer_info?.dateOfBirth ||
    result?.consumer_info?.dob ||
    result?.consumer_info?.date_of_birth ||
    result?.consumer_info?.dateOfBirth ||
    dobAnchor?.correct_value ||
    reportData?.personal_info?.dob ||
    reportData?.personal_info?.date_of_birth ||
    reportData?.personal_info?.dateOfBirth ||
    ''
  );
}

export function buildIdentityAnchorDataForCompletion({
  resolvedResult = null,
  result = null,
  reportData = null,
  user = null,
  piAnchorConfirmations = [],
} = {}) {
  const addressAnchor = piAnchorConfirmations.find((c) => c.category === 'address');
  const nameAnchor = piAnchorConfirmations.find(
    (c) => c.category === 'legal_name' || c.category === 'name'
  );
  const dobAnchor = piAnchorConfirmations.find((c) => c.category === 'dob');

  const streetAddr =
    resolvedResult?.consumer_info?.structured_address?.street_address ||
    addressAnchor?.normalized_address?.street_address ||
    addressAnchor?.manual_entry_values?.street ||
    '';
  const unitAddr =
    resolvedResult?.consumer_info?.structured_address?.unit ||
    addressAnchor?.normalized_address?.unit ||
    addressAnchor?.manual_entry_values?.unit ||
    '';
  const cityAddr =
    resolvedResult?.consumer_info?.structured_address?.city ||
    addressAnchor?.normalized_address?.city ||
    addressAnchor?.manual_entry_values?.city ||
    '';
  const stateAddr =
    resolvedResult?.consumer_info?.structured_address?.state ||
    addressAnchor?.normalized_address?.state ||
    addressAnchor?.manual_entry_values?.state ||
    '';
  const zipAddr =
    resolvedResult?.consumer_info?.structured_address?.zip ||
    addressAnchor?.normalized_address?.zip ||
    addressAnchor?.manual_entry_values?.zip ||
    '';

  const fullAddressStr = [
    [streetAddr, unitAddr].filter(Boolean).join(' '),
    [cityAddr, stateAddr, zipAddr].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(', ');

  const resolvedName = resolveConsumerNameForCompletion({
    resolvedResult,
    result,
    reportData,
    user,
    nameAnchor,
  });
  const resolvedDob = resolveConsumerDobForCompletion({
    resolvedResult,
    result,
    reportData,
    dobAnchor,
  });

  return {
    name: resolvedName,
    legal_name: resolvedName,
    full_name: resolvedName,
    structured_address:
      resolvedResult?.consumer_info?.structured_address ||
      (addressAnchor
        ? {
            street_address: streetAddr,
            unit: unitAddr,
            city: cityAddr,
            state: stateAddr,
            zip: zipAddr,
            source: 'identity_anchor',
          }
        : null),
    address_line_1: streetAddr,
    address_line_2: unitAddr,
    city: cityAddr,
    state: stateAddr,
    zip: zipAddr,
    current_address: fullAddressStr,
    dob: resolvedDob,
    source: 'identity_anchor',
  };
}
